import { Box, Card, Skeleton } from '@mui/material';

const MOBILE_OUTCOME_ITEMS = [1, 2];
const DESKTOP_OUTCOME_ITEMS = [1, 2, 3];
const RAIL_ROW_ITEMS = [1, 2, 3, 4];

const MobileSlideSkeleton = () => (
  <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
      <Skeleton
        variant="rounded"
        width={40}
        height={40}
        animation="wave"
        sx={{ borderRadius: '10px', flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="35%" height={14} animation="wave" />
        <Skeleton variant="text" width="25%" height={14} animation="wave" sx={{ mt: '2px' }} />
      </Box>
      <Skeleton variant="circular" width={20} height={20} animation="wave" />
    </Box>

    <Skeleton variant="text" width="90%" height={28} animation="wave" sx={{ mb: '2px' }} />
    <Skeleton variant="text" width="60%" height={28} animation="wave" sx={{ mb: 1.75 }} />

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {MOBILE_OUTCOME_ITEMS.map((item) => (
        <Box key={item} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Skeleton variant="text" width="45%" height={20} animation="wave" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton
              variant="rounded"
              width="100%"
              height={6}
              animation="wave"
              sx={{ borderRadius: 999, flex: 1 }}
            />
            <Skeleton
              variant="rounded"
              width={44}
              height={22}
              animation="wave"
              sx={{ borderRadius: 999, flexShrink: 0 }}
            />
          </Box>
        </Box>
      ))}
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.75 }}>
      <Skeleton variant="text" width={70} height={16} animation="wave" />
      <Skeleton variant="text" width={50} height={16} animation="wave" />
    </Box>
  </Box>
);

const DesktopSlideSkeleton = () => (
  <Box
    sx={{
      display: { xs: 'none', sm: 'grid' },
      gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)',
      gap: 3,
    }}
  >
    {/* Left: header + outcomes */}
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <Skeleton
          variant="rounded"
          width={48}
          height={48}
          animation="wave"
          sx={{ borderRadius: '8px', flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Skeleton variant="text" width="85%" height={26} animation="wave" />
          <Skeleton variant="text" width="55%" height={26} animation="wave" />
        </Box>
        <Skeleton variant="circular" width={20} height={20} animation="wave" />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {DESKTOP_OUTCOME_ITEMS.map((item) => (
          <Box
            key={item}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 1, px: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Skeleton variant="circular" width={10} height={10} animation="wave" />
              <Skeleton variant="text" width="60%" height={20} animation="wave" sx={{ flex: 1 }} />
              <Skeleton variant="text" width={32} height={20} animation="wave" />
            </Box>
            <Skeleton
              variant="rounded"
              width="100%"
              height={4}
              animation="wave"
              sx={{ borderRadius: 999 }}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Skeleton variant="text" width={70} height={16} animation="wave" />
        <Skeleton variant="text" width={110} height={16} animation="wave" />
      </Box>
    </Box>

    {/* Right: chart */}
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 1,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Skeleton variant="text" width={50} height={16} animation="wave" />
          <Skeleton variant="text" width={50} height={16} animation="wave" />
        </Box>
        <Skeleton
          variant="rounded"
          width={140}
          height={28}
          animation="wave"
          sx={{ borderRadius: 2 }}
        />
      </Box>
      <Skeleton
        variant="rounded"
        width="100%"
        height={240}
        animation="wave"
        sx={{ borderRadius: 2, flex: 1 }}
      />
    </Box>
  </Box>
);

const SideRailSkeleton = () => (
  <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 2, minWidth: 0 }}>
    {[0, 1].map((section) => (
      <Box
        key={section}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2 }}
      >
        <Skeleton variant="text" width={90} height={20} animation="wave" sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {RAIL_ROW_ITEMS.map((item) => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Skeleton variant="text" width="100%" height={18} animation="wave" sx={{ flex: 1 }} />
              <Skeleton variant="text" width={32} height={18} animation="wave" />
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

/**
 * Mirrors FeaturedMarketsCarousel's layout (hero card + side rail) so the
 * home page's hero slot doesn't collapse to nothing while events are
 * loading — the carousel itself renders null until it has usable events.
 */
export const FeaturedMarketsCarouselSkeleton = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' },
      gap: 2,
      alignItems: 'start',
      mb: 3,
    }}
  >
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        backgroundImage: 'none',
        boxShadow: 'none',
        p: { xs: 2.5, md: 3 },
        minWidth: 0,
      }}
    >
      <MobileSlideSkeleton />
      <DesktopSlideSkeleton />
    </Card>

    <SideRailSkeleton />
  </Box>
);

export default FeaturedMarketsCarouselSkeleton;
